import React, { useState } from 'react';
import { Row, Col, Modal, FormGroup } from 'react-bootstrap';
import { cloneDeep, set, isEmpty, get, camelCase } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { checkIfSectionNameExist } from '../../../Components/helper';
import Loader from '../../../Components/Utility/Loader/Loader';
import * as action from './store/CustomSections.action';

function AddCustomSectionTemplateModal({ isOpen, handleModalState }) {
  const reduxData = useSelector((prevState) => prevState);
  const { t } = useTranslation('common');

  const {
    customSectionData: { customSectionList },
  } = reduxData;
  const dispatch = useDispatch();

  const [state, setState] = useState({
    sectionTempNameArray: [{ name: '', type: 'critical-path' }],
    error: [],
    loading: false,
  });

  const handleChange = (value, path) => {
    const copyState = cloneDeep(state);
    set(copyState.sectionTempNameArray, path, value);
    set(copyState.error, path, '');
    setState((prevState) => ({ ...prevState, ...copyState, error: [] }));
  };

  const checkValidation = () => {
    const sectionName = cloneDeep(state.sectionTempNameArray);
    let hasError = false;
    const errors = [];
    sectionName.forEach((template, index) => {
      if (isEmpty(get(template, 'name', '').trim())) {
        hasError = true;
        set(errors, `[${index}].name`, <span className="error-message">{t('RANGE_PLANS.CUSTOM_SECTION.ADD_CUSTOM_SECTION.EMPTY_TEMPLATE_NAME')}</span>);
      }
    });
    setState((prevState) => ({ ...prevState, error: errors }));
    return hasError;
  };

  const addSectionName = (e, index) => {
    e.preventDefault();
    if (!checkValidation()) {
      const copyState = cloneDeep(state.sectionTempNameArray);
      set(copyState, `[${index + 1}].name`, '');
      set(copyState, `[${index + 1}].type`, 'critical-path');
      setState((prevState) => ({
        ...prevState,
        sectionTempNameArray: copyState,
      }));
    }
  };

  const removeSectionName = (e, index) => {
    e.preventDefault();
    const copyState = cloneDeep(state.sectionTempNameArray);
    copyState.splice(index, 1);
    setState((prevState) => ({
      ...prevState,
      sectionTempNameArray: copyState,
    }));
  };

  const addSectionTemplate = async () => {
    const nameExist = checkIfSectionNameExist(customSectionList, state.sectionTempNameArray, 'name');
    const reservedNames = findReservedSectionNames(state.sectionTempNameArray);
    if (nameExist.length !== 0) {
      toasterMsg(nameExist.join(', ') + t('RANGE_PLANS.CUSTOM_SECTION.ADD_CUSTOM_SECTION.SECTION_NAME_EXIST'), 'danger');
    } else if (reservedNames.length !== 0) {
      toasterMsg(`${reservedNames.join(', ')}: ${t('BACKEND_SERVER.SECTION.CUSTOM_SECTION_RANGE_PLAN.SECTION_NAME_RESERVED')}`, 'danger');
    } else if (!checkValidation()) {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await dispatch(action.addSectionTemplateAction(state.sectionTempNameArray));
      if (res) {
        setState((prevState) => ({ ...prevState, loading: false }));
        handleModalState(false);
      }
    }
  };

  const cleanState = () => {
    setState((prevState) => ({ ...prevState, error: [] }));
  };
  return (
    <Modal className="common-custom-modal" size="md" show={isOpen} onHide={() => handleModalState(false)} backdrop="static">
      <Modal.Header>
        <Modal.Title>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_CUSTOM_SECTION.MODAL_TITLE')}</Modal.Title>
        <span
          onClick={() => {
            cleanState();
            handleModalState(false);
          }}
          className="icon-close cursor-pointer"
        />
      </Modal.Header>
      <Modal.Body>
        {state.loading ? (
          <Loader />
        ) : (
          <>
            <Row>
              <Col lg="12">
                {Array.isArray(state.sectionTempNameArray) &&
                  state.sectionTempNameArray.length > 0 &&
                  state.sectionTempNameArray.map((item, index) => {
                    return (
                      <div className="card card-body mb-3 p-3 rounded-0 theme-border" key={index}>
                        <FormGroup className="position-relative">
                          <div className="floting-button">
                            {state.sectionTempNameArray.length - 1 === index ? (
                              <button className="btn btn-outline btn-absolute m-0" onClick={(e) => addSectionName(e, index)}>
                                <span className="icon-Add-bold" />
                              </button>
                            ) : (
                              <button className="btn btn-outline btn-absolute m-0" onClick={(e) => removeSectionName(e, index)}>
                                <span className="icon-Delete-bold" />
                              </button>
                            )}
                          </div>

                          <div className="input-group form-floating-label fixed-label">
                            <input
                              type="text"
                              name="costingTempName"
                              placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_CUSTOM_SECTION.ENTER_TEMPLATE_NAME_PLACEHOLDER')}
                              className="form-control"
                              maxLength="30"
                              value={item.name}
                              onChange={(e) => handleChange(e.target.value, `[${index}].name`)}
                            />
                            <label className="asterisk-req">{t('RANGE_PLANS.CUSTOM_SECTION.ADD_CUSTOM_SECTION.TEMPLATE_NAME_LABEL')}</label>
                            <div className="text-count">{get(item, 'name').length}/30</div>
                          </div>
                          <div>{get(state.error, `${index}.name`)}</div>
                        </FormGroup>
                      </div>
                    );
                  })}
              </Col>
            </Row>
            <div className="common-model-footer">
              <button type="button" aria-label="Save" className="btn" onClick={() => addSectionTemplate()}>
                <i className="icon-Tick-1 f-24" />
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default AddCustomSectionTemplateModal;

/**
 * Rights-matrix keys of the built-in Range Plan rows. A custom section whose name camel-cases to
 * one of them would share its rights row, so the server refuses it; this list mirrors
 * `BUILT_IN_RANGE_PLAN_SUBSECTIONS` in `server/services/user-rights.service.js`.
 */
const RESERVED_RANGE_PLAN_SECTION_KEYS = ['default', 'custom', 'productIdentifier'];

/**
 * Names in the form that the server would refuse as reserved, so the user is told why before saving
 * instead of seeing them reported as duplicates.
 * @param {Array<{name: string}>} sections - Sections typed in the modal.
 * @returns {string[]} The reserved names, as typed.
 */
function findReservedSectionNames(sections) {
  return (sections || [])
    .map((section) => section?.name ?? '')
    .filter((name) => RESERVED_RANGE_PLAN_SECTION_KEYS.includes(camelCase(name.trim())));
}
